import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import type { RecommendationFilters } from '@ederest/contracts';
import { Card } from '../../shared/ui/Card.js';
import { PageHeader } from '../../shared/layout/PageHeader.js';
import { useRecommendations } from '../../shared/api/queries.js';
import { useActiveProject } from '../../shared/api/useActiveProject.js';
import { RecommendationDetail } from './RecommendationDetail.js';
import { RecommendationExportMenu } from './RecommendationExportMenu.js';
import { RecommendationFilterBar, type QueueFilters } from './RecommendationFilterBar.js';
import { QUEUE_COLUMN_HINTS, RecommendationQueue } from './RecommendationQueue.js';
import { RecommendationSummary } from './RecommendationSummary.js';
import './recommendations.css';

const PAGE_SIZE = 25;

/**
 * "Recommandations" (functional §5.1, §5.6): the validation queue. Every
 * proposal comes with the measurement that justifies it and the effect
 * expected; the teams accept, adjust or dismiss with a reason. Nothing here
 * publishes by itself, and nothing here creates a guidance from scratch.
 */
export function RecommendationsPage() {
  const { project } = useActiveProject();
  const [filters, setFilters] = useState<QueueFilters>({ state: 'pending', kind: undefined });
  const [page, setPage] = useState(1);
  // The selection lives in the URL so a recommendation can be linked to from the journey tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('selected');
  const setSelectedId = (id: string | null) => setSearchParams(id ? { selected: id } : {}, { replace: true });

  const query: Partial<RecommendationFilters> = { ...filters, page, pageSize: PAGE_SIZE };
  const { data, isLoading, isFetching, isError } = useRecommendations(project?.id, query);

  const changeFilters = (next: QueueFilters) => {
    setFilters(next);
    setPage(1);
    setSelectedId(null);
  };

  const pageCount = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Recommandations"
        description={
          project
            ? `Projet ${project.name}${data ? ` · ${data.pendingCount.toLocaleString('fr-FR')} en attente de décision` : ''}`
            : undefined
        }
        actions={<RecommendationExportMenu projectId={project?.id} />}
      />

      {filters.state === 'pending' && <RecommendationSummary page={data} />}

      <RecommendationFilterBar value={filters} onChange={changeFilters} />

      <div className={`queue ${selectedId ? 'queue--split' : ''}`}>
        <Card
          title="File de validation"
          icon={<Inbox size={15} />}
          padded={false}
          actions={
            data && (
              <span className="queue__count" data-testid="queue-count">
                {data.totalCount.toLocaleString('fr-FR')} recommandation{data.totalCount > 1 ? 's' : ''}
                {isFetching && !isLoading ? ' · mise à jour…' : ''}
              </span>
            )
          }
        >
          {isLoading ? (
            <p className="queue__empty">Chargement de la file…</p>
          ) : isError ? (
            <p className="queue__empty">La file n'a pas pu être chargée. Réessayez.</p>
          ) : (
            <>
              <RecommendationQueue
                items={data?.items ?? []}
                selectedId={selectedId}
                compact={selectedId !== null}
                onSelect={(id) => setSelectedId(id === selectedId ? null : id)}
              />
              {pageCount > 1 && (
                <nav className="queue__pager" aria-label="Pages de la file">
                  <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    Précédent
                  </button>
                  <span>
                    Page {page} sur {pageCount}
                  </span>
                  <button type="button" disabled={page >= pageCount} onClick={() => setPage(page + 1)}>
                    Suivant
                  </button>
                </nav>
              )}
            </>
          )}
          <dl className="queue__legend" aria-label="Lecture des colonnes">
            <div>
              <dt>Priorité</dt>
              <dd>{QUEUE_COLUMN_HINTS.priority}</dd>
            </div>
            <div>
              <dt>Friction</dt>
              <dd>{QUEUE_COLUMN_HINTS.friction}</dd>
            </div>
            <div>
              <dt>Usagers</dt>
              <dd>{QUEUE_COLUMN_HINTS.users}</dd>
            </div>
            <div>
              <dt>Réponse proposée</dt>
              <dd>{QUEUE_COLUMN_HINTS.answer}</dd>
            </div>
          </dl>
          <p className="queue__note">
            L'intelligence artificielle propose, les équipes décident : aucune recommandation n'est appliquée
            sans décision humaine, chaque décision est tracée avec son auteur et sa date.
          </p>
        </Card>

        {selectedId && (
          <RecommendationDetail recommendationId={selectedId} onClose={() => setSelectedId(null)} />
        )}
      </div>
    </>
  );
}
